<template>
  <main class="mall_main w-full">
    <div class="pt-5 flex flex-col h-full">
      <div class="w-fit flex justify-center items-center bg-slate-100 rounded-full mx-auto px-4 py-1 text-sm sticky top-1 z-10">
        {{ currentDate }}
      </div>

      <!-- 스크롤 가능한 채팅 영역 -->
      <div ref="chatContainer" class="flex-1 overflow-y-auto w-full my-5 p-5">
        <!-- 초기 인사 메시지 -->
        <div class="mb-6">
          <ChatBotAvatar :size="51" />
          <p class="text-xl font-medium mt-3">안녕하세요.<br />총회교육개발원 콘텐츠 몰 챗봇입니다.<br />무엇을 도와드릴까요?</p>
          <p class="text-sm text-neutral-400 mt-2">{{ currentTime }}</p>
        </div>

        <!-- 질문 예시 grid - 대화가 없을 때만 표시 -->
        <div v-if="!hasUserMessage" class="grid grid-cols-2 gap-3 my-5">
          <div class="w-full" v-for="question in displayedQuestions" :key="question">
            <div class="w-full h-full border border-neutral-200 rounded-lg p-3 cursor-pointer hover:bg-gray-50 transition-colors text-sm" @click="handleQuestionClick(question)">
              {{ question }}
            </div>
          </div>
        </div>

        <!-- 대화 히스토리 -->
        <div v-for="message in messages" :key="message.id" :data-msg-id="message.id" class="my-4">
          <!-- 사용자 메시지 -->
          <div v-if="message.from === 'user'" class="flex justify-end">
            <div class="max-w-[calc(100%-60px)]">
              <p class="text-blue-700 bg-blue-50 rounded-lg p-4 break-words">{{ message.text }}</p>
              <p class="text-sm text-neutral-400 mt-2 text-right">{{ message.time }}</p>
            </div>
          </div>

          <!-- 봇 메시지 -->
          <div v-else class="flex items-start gap-3">
            <ChatBotAvatar class="flex-shrink-0" />
            <div class="min-w-0 [flex:1_1_0%] flex flex-col gap-2">
              <p v-if="message.text" class="self-start bg-neutral-100 rounded-lg p-4 break-words whitespace-pre-line">{{ message.text }}</p>

              <!-- 공과 카드 -->
              <ChatLessonCard
                v-if="message.lesson"
                :lesson="message.lesson"
                :sections="message.sections"
                :loading="message.loading"
                :groupLabel="message.groupLabel"
                :eyebrow="message.eyebrow"
                @parent-notice="showParentNotice(message)"
              >
                <template v-if="message.actions && message.actions.length" #actions>
                  <button
                    v-for="action in message.actions"
                    :key="action.label"
                    type="button"
                    :disabled="message.actionsDone || isLoading"
                    class="rounded-full border px-3 py-1 text-sm disabled:opacity-40"
                    :class="[action.primary ? 'bg-main-navy border-main-navy text-white' : 'border-neutral-300 hover:border-main-navy', { '!opacity-100 !border-main-navy font-bold': message.picked === action.label }]"
                    @click="runAction(message, action)"
                  >
                    {{ action.label }}
                  </button>
                </template>
              </ChatLessonCard>

              <!-- 공과 고르기 -->
              <div v-if="message.choices" class="flex flex-col gap-2">
                <button
                  v-for="choice in message.choices"
                  :key="choice.lessonRef"
                  type="button"
                  :disabled="isLoading"
                  class="text-left rounded-lg border border-neutral-200 bg-white px-3 py-2 hover:border-main-navy disabled:opacity-50"
                  @click="askLesson(choice)"
                >
                  <span class="block text-xs text-neutral-500">{{ semesterName(choice) }} · {{ choice.area }}</span>
                  <span class="block font-bold text-main-navy">{{ choice.isSpecial ? choice.occasion : lessonName(choice) }} {{ choice.title }}</span>
                  <span v-if="message.showKeyPoint" class="block text-xs text-neutral-500 mt-0.5">{{ choice.keyPoint }}</span>
                </button>
              </div>

              <!-- 자료 검색 결과 -->
              <template v-if="message.items">
                <ChatContentList :items="message.items" />
                <p class="text-xs text-neutral-400">검색 결과 {{ message.total }}개 중 {{ message.items.length }}개</p>
              </template>

              <!-- 버튼 (공과 카드 밖) -->
              <div v-if="!message.lesson && message.actions && message.actions.length" class="flex flex-wrap gap-2">
                <button
                  v-for="action in message.actions"
                  :key="action.label"
                  type="button"
                  :disabled="message.actionsDone || isLoading"
                  class="rounded-full border px-3 py-1 text-sm disabled:opacity-40"
                  :class="[action.primary ? 'bg-main-navy border-main-navy text-white' : 'border-neutral-300 hover:border-main-navy', { '!opacity-100 !border-main-navy font-bold': message.picked === action.label }]"
                  @click="runAction(message, action)"
                >
                  {{ action.label }}
                </button>
              </div>

              <p v-if="message.note" class="text-xs text-neutral-400">{{ message.note }}</p>
              <div class="flex items-center gap-3 text-sm text-neutral-400">
                <span>{{ message.time }}</span>
                <!-- 답변 평가 (BE-2) -->
                <template v-if="message.requestId">
                  <span v-if="message.rating" class="text-xs">의견 고마워요</span>
                  <template v-else>
                    <button type="button" class="text-xs hover:text-main-navy" @click="rateAnswer(message, 1)">도움이 됐어요</button>
                    <button type="button" class="text-xs hover:text-main-navy" @click="rateAnswer(message, -1)">아쉬워요</button>
                  </template>
                </template>
              </div>
            </div>
          </div>
        </div>

        <!-- 로딩 인디케이터 -->
        <div v-if="isLoading" class="my-4 flex items-start gap-3">
          <ChatBotAvatar class="flex-shrink-0" />
          <div class="bg-neutral-100 rounded-lg p-4">
            <div class="flex items-center gap-1">
              <div class="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style="animation-delay: 0ms"></div>
              <div class="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style="animation-delay: 150ms"></div>
              <div class="w-2 h-2 bg-neutral-400 rounded-full animate-bounce" style="animation-delay: 300ms"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- 입력 영역 -->
      <div class="sticky bottom-0 w-full bg-white p-5 border-t border-neutral-100">
        <div class="w-full flex justify-between items-center gap-3 border border-neutral-200 rounded-lg px-4 py-3">
          <input v-model="userInput" type="text" maxlength="500" placeholder="궁금한 사항을 입력해 주세요" class="w-full focus:outline-none" @keyup.enter="handleSendMessage" :disabled="isLoading" />
          <button @click="handleSendMessage" :disabled="!userInput.trim() || isLoading">
            <svg v-if="!isLoading" width="31" height="31" viewBox="0 0 31 31" fill="none" xmlns="http://www.w3.org/2000/svg" :class="{ 'opacity-50': !userInput.trim() }">
              <path d="M17.476 17.8036L2.88058 22.1794L4.80064 15.7507L2.88058 9.32196L17.476 13.6978C19.5117 14.3085 19.5117 17.1907 17.476 17.8036Z" fill="url(#paint0_radial_55_8)" />
              <path
                d="M2.30201 0.863873C2.12265 0.774165 1.92171 0.736596 1.72205 0.755439C1.52239 0.774281 1.33203 0.84878 1.17262 0.97046C1.01321 1.09214 0.891145 1.25612 0.820317 1.44374C0.749488 1.63136 0.732737 1.83509 0.771969 2.03176L3.77848 12.4249C3.83454 12.6186 3.94411 12.7925 4.0946 12.9267C4.2451 13.0609 4.4304 13.1499 4.62921 13.1835L16.8224 15.2257C17.3967 15.3393 17.3967 16.1621 16.8224 16.2757L4.62921 18.3179C4.4304 18.3515 4.2451 18.4405 4.0946 18.5747C3.94411 18.7089 3.83454 18.8828 3.77848 19.0765L0.771969 29.4696C0.732737 29.6663 0.749488 29.87 0.820317 30.0577C0.891145 30.2453 1.01321 30.4093 1.17262 30.5309C1.33203 30.6526 1.52239 30.7271 1.72205 30.746C1.92171 30.7648 2.12265 30.7272 2.30201 30.6375L30.1599 16.7086C30.3376 16.6195 30.4871 16.4827 30.5915 16.3136C30.696 16.1444 30.7513 15.9495 30.7513 15.7507C30.7513 15.5519 30.696 15.357 30.5915 15.1878C30.4871 15.0187 30.3376 14.8819 30.1599 14.7928L2.30201 0.863873Z"
                fill="url(#paint1_linear_55_8)"
              />
              <path
                d="M2.30201 0.863873C2.12265 0.774165 1.92171 0.736596 1.72205 0.755439C1.52239 0.774281 1.33203 0.84878 1.17262 0.97046C1.01321 1.09214 0.891145 1.25612 0.820317 1.44374C0.749488 1.63136 0.732737 1.83509 0.771969 2.03176L3.77848 12.4249C3.83454 12.6186 3.94411 12.7925 4.0946 12.9267C4.2451 13.0609 4.4304 13.1499 4.62921 13.1835L16.8224 15.2257C17.3967 15.3393 17.3967 16.1621 16.8224 16.2757L4.62921 18.3179C4.4304 18.3515 4.2451 18.4405 4.0946 18.5747C3.94411 18.7089 3.83454 18.8828 3.77848 19.0765L0.771969 29.4696C0.732737 29.6663 0.749488 29.87 0.820317 30.0577C0.891145 30.2453 1.01321 30.4093 1.17262 30.5309C1.33203 30.6526 1.52239 30.7271 1.72205 30.746C1.92171 30.7648 2.12265 30.7272 2.30201 30.6375L30.1599 16.7086C30.3376 16.6195 30.4871 16.4827 30.5915 16.3136C30.696 16.1444 30.7513 15.9495 30.7513 15.7507C30.7513 15.5519 30.696 15.357 30.5915 15.1878C30.4871 15.0187 30.3376 14.8819 30.1599 14.7928L2.30201 0.863873Z"
                fill="url(#paint2_linear_55_8)"
              />
              <defs>
                <radialGradient id="paint0_radial_55_8" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(-0.32093 15.7507) scale(15.9391 2.40776)">
                  <stop stop-color="#0094F0" />
                  <stop offset="1" stop-color="#2052CB" />
                </radialGradient>
                <linearGradient id="paint1_linear_55_8" x1="0.75054" y1="-11.4386" x2="26.4441" y2="22.7023" gradientUnits="userSpaceOnUse">
                  <stop stop-color="#3BD5FF" />
                  <stop offset="1" stop-color="#0094F0" />
                </linearGradient>
                <linearGradient id="paint2_linear_55_8" x1="15.7509" y1="8.83551" x2="23.5533" y2="29.9454" gradientUnits="userSpaceOnUse">
                  <stop offset="0.125" stop-color="#DCF8FF" stop-opacity="0" />
                  <stop offset="0.769" stop-color="#FF6CE8" stop-opacity="0.7" />
                </linearGradient>
              </defs>
            </svg>
            <svg v-else class="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </main>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, watch } from "vue";
import axios from "axios";
import { pickChatbotQuestions } from "@/assets/js/chatbotQuestions";
import {
  loadLessons,
  findLesson,
  findOccasionLessons,
  fetchSpecialMaterials,
  HANA_GROUPS,
  classifyQuestion,
  searchLessonsByTopic,
  searchLessonsByTags,
  fetchLessonBundle,
  groupLabelOf,
  semesterName,
  lessonName,
  SERVICE_INFO,
  SERVICE_INFO_SOURCE,
  ADOPTION_INFO,
  ADOPTION_INFO_SOURCE,
  KAKAO_CHANNEL_URL,
  HELP_EXAMPLES,
} from "@/assets/js/hanaLessons";
import { saveReturnPath, markReopenChatbot } from "@/composables/useAuthReturn";
import { buildParentNotice, familyWorshipUrl, copyText, shareToKakao } from "@/assets/js/chatShare";
import { parsePreference, formatPreference, currentProgress, upcomingSunday, addWeeks, shiftLesson } from "@/assets/js/hanaProgress";
import ChatBotAvatar from "@/components/chatbot/ChatBotAvatar.vue";
import ChatLessonCard from "@/components/chatbot/ChatLessonCard.vue";
import ChatContentList from "@/components/chatbot/ChatContentList.vue";

const userInput = ref("");
const isLoading = ref(false);
const chatContainer = ref(null);

// 대화 히스토리
// { id, from: 'user'|'bot', time, text?, lesson?, sections?, loading?, groupLabel?, eyebrow?, choices?, showKeyPoint?, items?, total?, actions?, actionsDone?, picked?, note? }
const messages = ref([]);
let messageSeq = 0;
const hasUserMessage = computed(() => messages.value.some((m) => m.from === "user"));

const getFormattedDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  const dayName = ["일", "월", "화", "수", "목", "금", "토"][today.getDay()];
  return `${year}. ${month}. ${day} (${dayName})`;
};

const getFormattedTime = () => {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
};

const currentDate = ref(getFormattedDate());
const currentTime = ref(getFormattedTime());

// 새 답변 보이기: 채팅 영역은 높이 제한 없이 늘어나고 실제로는 페이지(챗봇 창 안의 문서)가 스크롤된다.
// 새로 생긴 내용이 화면에 다 들어가면 맨 아래로, 공과 카드처럼 화면보다 길면
// 질문(또는 누른 버튼) 바로 다음부터 보이도록 내린다 — 맨 아래로 내리면 카드 윗부분을 다시 올려 봐야 해서.
const STICKY_TOP_SPACE = 70; // 위에 고정된 날짜 표시만큼 띄운다
let scrollAnchorId = 0; // 이번에 새로 생긴 내용의 시작 메시지

const scrollToLatest = async () => {
  await nextTick();
  const doc = document.scrollingElement || document.documentElement;
  const bottom = doc.scrollHeight - window.innerHeight;
  const anchor = scrollAnchorId && document.querySelector(`[data-msg-id="${scrollAnchorId}"]`);
  // 버튼을 눌렀는데 아직 새 메시지가 없으면(복사하기 등) 읽던 자리를 그대로 둔다
  if (scrollAnchorId && !anchor) return;
  let anchorTop = bottom;
  if (anchor) {
    const rect = anchor.getBoundingClientRect();
    // 질문이 화면의 절반보다 길면 질문 끝부분이 보이게 맞춰 답변이 밀려나지 않게 한다
    const edge = rect.height > window.innerHeight * 0.5 ? rect.bottom - 120 : rect.top;
    anchorTop = window.scrollY + edge - STICKY_TOP_SPACE;
  }
  window.scrollTo({ top: Math.max(0, Math.min(anchorTop, bottom)), behavior: "smooth" });
  // 채팅 영역에 높이 제한이 생기는 레이아웃이면 그 안도 함께 내린다
  if (chatContainer.value) chatContainer.value.scrollTop = chatContainer.value.scrollHeight;
};

// 메시지를 추가하고, 이후 갱신(자료 로딩 완료 등)이 반영되도록 반응형 객체를 돌려준다
// 답을 기다리는 "…" 표시도 보이게
watch(isLoading, (loading) => loading && scrollToLatest());

const addMessage = (from, fields) => {
  messages.value.push({ id: ++messageSeq, from, time: getFormattedTime(), ...(typeof fields === "string" ? { text: fields } : fields) });
  if (from === "user") scrollAnchorId = messageSeq; // 질문부터 새 내용이 시작된다
  scrollToLatest();
  return messages.value[messages.value.length - 1];
};

const runAction = async (message, action) => {
  scrollAnchorId = messageSeq + 1; // 버튼을 누른 뒤 생기는 첫 메시지부터 보인다
  message.actionsDone = true;
  message.picked = action.label; // 고른 버튼은 흐리게 하지 않는다
  isLoading.value = true;
  try {
    const ok = await action.run();
    if (ok === false) {
      message.actionsDone = false; // 실패하면 다시 누를 수 있게
      message.picked = null;
    }
  } finally {
    isLoading.value = false;
  }
};

// ---------- 로그인 교사의 하나 바이블 설정 (부서:학기:과) ----------

const isLoggedIn = () => !!localStorage.getItem("authorization");

// ---------- 비회원 가입 유도 ----------
// 원칙: 가치를 먼저 보여 준 뒤, 정해진 순간에만, 같은 안내는 한 대화에 한 번만 (docs/chatbot-improvement-plan.md 4장)

const inFrame = window.parent !== window;
const shownSignupPrompts = new Set();
const answeredCount = ref(0);
const GENERIC_PROMPT_AFTER = 3;

// 챗봇을 띄운 바깥 페이지 (가입·로그인 후 돌아올 곳)
const hostPath = () => {
  try {
    const loc = inFrame ? window.parent.location : window.location;
    return loc.pathname + loc.search;
  } catch (e) {
    return "/";
  }
};

// ---------- 가입·로그인을 다녀와도 대화 이어 가기 ----------
// 가입·로그인은 페이지 이동이라 대화가 사라진다. 떠나기 직전 대화를 탭 안(sessionStorage)에 잠깐 두었다가 다시 열 때 복원한다.
const CHAT_SNAPSHOT_KEY = "chatbotSnapshot";
const CHAT_SNAPSHOT_TTL_MS = 30 * 60 * 1000;

const saveChatSnapshot = () => {
  try {
    sessionStorage.setItem(
      CHAT_SNAPSHOT_KEY,
      JSON.stringify({
        at: Date.now(),
        prompts: [...shownSignupPrompts],
        // 비회원이 고른 부서·진도: 가입하고 돌아오면 한 번에 저장하도록 권한다
        guestPick: isLoggedIn() ? null : preference.value,
        answered: answeredCount.value,
        // 버튼(함수)은 저장할 수 없어서 뺀다. 복원한 대화는 읽기 전용이다.
        messages: messages.value.map((m) => {
          // eslint-disable-next-line no-unused-vars
          const { actions, actionsDone, loading, ...rest } = m;
          return rest;
        }),
      }),
    );
  } catch (e) {
    // 저장 공간이 없거나 막혀 있으면 대화 없이 진행한다
  }
};

let guestPick = null; // 복원한 대화에서 비회원 때 고른 진도 { group, semesterKey, lesson }

const restoreChatSnapshot = () => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(CHAT_SNAPSHOT_KEY) || "null");
    sessionStorage.removeItem(CHAT_SNAPSHOT_KEY);
    if (!saved || Date.now() - saved.at > CHAT_SNAPSHOT_TTL_MS || !Array.isArray(saved.messages) || !saved.messages.length) return false;
    messages.value = saved.messages;
    messageSeq = Math.max(...saved.messages.map((m) => m.id || 0));
    (saved.prompts || []).forEach((key) => shownSignupPrompts.add(key));
    answeredCount.value = saved.answered || 0;
    guestPick = saved.guestPick || null;
    // 로그인을 취소하고 돌아온 비회원이면 고른 부서·진도를 그대로 이어 쓴다
    if (guestPick && !isLoggedIn()) preference.value = guestPick;
    scrollToLatest();
    return true;
  } catch (e) {
    return false;
  }
};

const goSignup = () => {
  saveChatSnapshot();
  saveReturnPath(hostPath());
  markReopenChatbot();
  (inFrame ? window.parent : window).location.href = "/signup";
};

const openLogin = () => {
  saveChatSnapshot();
  if (inFrame) {
    // 바깥 페이지(toTopComponent)가 상단 메뉴의 로그인 창을 연다
    window.parent.postMessage({ type: "chatbot:open-login" }, window.location.origin);
  } else {
    saveReturnPath(hostPath());
    window.location.href = "/login";
  }
};

const signupActions = (signupLabel = "무료 가입") => [
  { label: signupLabel, primary: true, run: goSignup },
  { label: "로그인", run: openLogin },
];

// 비회원에게 key 안내를 아직 안 했으면 보여 주고 true
const promptSignupOnce = (key, text, signupLabel) => {
  if (isLoggedIn() || shownSignupPrompts.has(key)) return false;
  shownSignupPrompts.add(key);
  addMessage("bot", { text, actions: signupActions(signupLabel) });
  return true;
};
const preference = ref(null); // { group, semesterKey, lesson } | null

const loadPreference = async () => {
  if (!isLoggedIn()) return null;
  try {
    const res = await axios.get("/new_api/account/hana-bible-preference");
    // 저장값은 "부서:과정-학기:과[:기준 주일]". 이번 주 진도는 currentProgress 로 계산한다
    preference.value = parsePreference(res.data?.data?.hanaBiblePreference);
  } catch (error) {
    console.error(error);
    preference.value = null;
  }
  return preference.value;
};

// 진도 저장: 이 공과를 anchor 주일(기본은 이번 주)의 진도로 저장하고, 대화 안의 진도도 바꾼다
const savePreference = async (group, lesson, anchor = upcomingSunday()) => {
  const next = { group, semesterKey: lesson.semesterKey, lesson: lesson.lesson, anchor };
  await axios.put("/new_api/account/hana-bible-preference", { hanaBiblePreference: formatPreference(next) });
  preference.value = next;
};

// ---------- 공과 카드 ----------

// 공과 카드를 띄우고 자료를 이어서 채운다
const showLessonCard = async (lesson, { text, eyebrow, actions } = {}) => {
  const group = preference.value?.group || "";
  const message = addMessage("bot", {
    text,
    lesson,
    eyebrow,
    groupLabel: groupLabelOf(group),
    loading: true,
    sections: null,
    actions,
  });
  try {
    message.sections = lesson.isSpecial
      ? await fetchSpecialMaterials({ lesson, group })
      : await fetchLessonBundle({ group, semesterKey: lesson.semesterKey, lesson: lesson.lesson });
  } catch (error) {
    console.error(error);
    message.sections = [];
  } finally {
    message.loading = false;
    scrollToLatest();
  }
  if (message.sections?.length) {
    promptSignupOnce("materials", "하나 바이블 공과 자료는 대부분 무료예요. 가입하시면 바로 받으실 수 있어요.", "무료 가입하고 받기");
  }
  // 질문 기록용: 어느 공과로 답했고 자료를 몇 개 보여 줬는지
  noteTurn({ lessonRef: lesson.lessonRef, resultCount: (message.sections || []).reduce((n, section) => n + section.items.length, 0) });
};

// 부모님께 보낼 안내문: 공과 정보 + 가정예배영상 링크로 만들어 대화창에 보여 주고 복사·공유하게 한다 (F6)
const showParentNotice = async (card) => {
  addMessage("user", "부모님께 보낼 안내문 만들어 줘");
  const notice = buildParentNotice(card.lesson, {
    groupLabel: card.groupLabel,
    videoUrl: await familyWorshipUrl(card.sections),
  });
  const message = addMessage("bot", {
    text: notice,
    note: "공과 정보는 브로슈어 그대로예요. 필요하면 고쳐서 보내세요.",
    actions: [
      {
        label: "복사하기",
        primary: true,
        run: async () => {
          message.note = (await copyText(notice)) ? "복사했어요. 단체 대화방에 붙여 넣어 보내세요." : "복사하지 못했어요. 글을 길게 눌러 복사해 주세요.";
          return false; // 여러 번 누를 수 있게
        },
      },
      {
        label: "카카오톡 공유",
        run: async () => {
          if (!(await shareToKakao({ text: notice, url: window.location.origin }))) {
            message.note = (await copyText(notice))
              ? "안내문이 길어 카카오톡 공유 대신 전체를 복사했어요. 대화방에 붙여 넣어 보내세요."
              : "공유하지 못했어요. 글을 길게 눌러 복사해 주세요.";
          }
          return false;
        },
      },
    ],
  });
};

const saveAsThisWeekAction = (lessons, lesson) => ({
  label: "이번 주 진도로 저장",
  run: async () => {
    const group = preference.value?.group;
    if (!group) {
      // 부서가 없으면 부서만 골라 받고 이 공과로 저장한다
      startProgressSetup(lessons, { lesson, intro: "어느 부서 선생님이신가요? 부서를 고르시면 이 공과를 이번 주 진도로 저장할게요." });
      return;
    }
    try {
      await savePreference(group, lesson);
      addMessage("bot", `이번 주 진도를 ${semesterName(lesson)} ${lessonName(lesson)}로 저장했어요. 사이트의 맞춤 추천에도 같이 반영됩니다.`);
    } catch (error) {
      console.error(error);
      addMessage("bot", "진도를 저장하지 못했어요. 잠시 뒤 다시 시도해 주세요.");
      return false;
    }
  },
});

// 이번 주 진도 고치기 버튼 (F2). 누른 시점의 저장된 진도로 계산한다
const progressActions = (lessons, { confirm = false } = {}) => {
  const act = (label, run, primary = false) => ({
    label,
    primary,
    run: async () => {
      const latest = currentProgress(preference.value);
      const current = latest && findLesson(lessons, latest.semesterKey, latest.lesson);
      if (!latest?.group || !current) return false;
      try {
        return await run(latest.group, current);
      } catch (error) {
        console.error(error);
        addMessage("bot", "진도를 저장하지 못했어요. 잠시 뒤 다시 시도해 주세요.");
        return false;
      }
    },
  });
  const nameOf = (l) => `${semesterName(l)} ${lessonName(l)}`;
  const moveBy = (weeks, textOf) => async (group, current) => {
    const to = shiftLesson(current, weeks);
    const target = to && findLesson(lessons, to.semesterKey, to.lesson);
    if (!target) return false;
    await savePreference(group, target);
    await showThisWeekCard(lessons, target, textOf(target));
  };
  return [
    ...(confirm
      ? [
          act(
            "맞아요",
            async (group, current) => {
              await savePreference(group, current);
              addMessage("bot", `이번 주를 ${nameOf(current)}로 확인했어요. 다음 주부터는 챗봇이 알아서 다음 과를 준비해요.`);
            },
            true,
          ),
        ]
      : []),
    act("한 주 밀렸어요", moveBy(-1, (t) => `이번 주를 ${nameOf(t)}로 고쳤어요. 다음 주부터 여기서 이어 가요.`)),
    act("이번 주는 쉬어요", async (group, current) => {
      // 다음 주일에 지금 공과를 하도록 기준 주일을 한 주 뒤로 둔다
      await savePreference(group, current, addWeeks(upcomingSunday(), 1));
      addMessage("bot", `이번 주는 쉬고, 다음 주에 ${nameOf(current)}를 하도록 저장했어요.`);
    }),
    act("한 주 당기기", moveBy(1, (t) => `이번 주를 ${nameOf(t)}로 당겼어요.`)),
  ];
};

// 이번 주 공과 카드 + 진도 고치기 버튼
const showThisWeekCard = (lessons, lesson, text, options) =>
  showLessonCard(lesson, { text, eyebrow: "이번 주 진도", actions: progressActions(lessons, options) });

// ---------- 진도 설정 (부서 → 과정·학기 → 과, 버튼 세 번) ----------
// 막 가입한 회원처럼 저장된 진도가 없을 때, 하나 큐브 페이지로 보내지 않고 대화 안에서 바로 저장한다.
// lesson 을 주면 부서만 고르고 그 공과로 저장한다.
const SETUP_OFFERED_KEY = "chatbotProgressSetupOffered";

// 비회원도 같은 버튼으로 고른다. 비회원은 서버에 저장하지 않고 이번 대화에서만 기억한다(F3).
const startProgressSetup = (lessons, { intro, lesson } = {}) => {
  if (isLoggedIn()) localStorage.setItem(SETUP_OFFERED_KEY, "1");
  const ask = (text, options, onPick) =>
    addMessage("bot", {
      text,
      awaitingPick: true, // 고를 때까지 질문 기록을 열어 둔다
      actions: options.map((option) => ({
        label: option.label,
        run: async () => {
          addMessage("user", option.label);
          return onPick(option.value);
        },
      })),
    });

  const finish = async (group, picked) => {
    const resumed = resumeTurn();
    try {
      return await finishPick(group, picked);
    } finally {
      if (resumed) finishTurn();
    }
  };

  const finishPick = async (group, picked) => {
    const label = groupLabelOf(group);
    if (!isLoggedIn()) {
      // 이후 공과 카드에도 이 부서 자료가 나오도록 대화 안에서 기억한다
      preference.value = { group, semesterKey: picked.semesterKey, lesson: picked.lesson };
      shownSignupPrompts.add("materials"); // 바로 아래 진도 저장 안내와 겹치지 않게 자료 안내는 건너뛴다
      await showLessonCard(picked, {
        text: `${label} ${semesterName(picked)} ${lessonName(picked)} 수업 자료예요.`,
        eyebrow: "이번 주 공과",
      });
      promptSignupOnce(
        "thisWeek",
        "가입하시면 고르신 진도를 저장해 두고, 다음 주부터는 챗봇을 열 때마다 이번 주 공과를 바로 준비해 드려요.",
        "가입하고 진도 저장",
      );
      return;
    }
    try {
      await savePreference(group, picked);
    } catch (error) {
      console.error(error);
      addMessage("bot", "진도를 저장하지 못했어요. 잠시 뒤 다시 골라 주세요.");
      return false; // 버튼을 다시 누를 수 있게
    }
    await showThisWeekCard(
      lessons,
      picked,
      `${label} 진도를 ${semesterName(picked)} ${lessonName(picked)}로 저장했어요. 이제 챗봇을 열 때마다 이번 주 공과를 준비해 드릴게요.`,
    );
  };

  const pickLesson = (group, semesterKey) => {
    const list = lessons.filter((l) => l.semesterKey === semesterKey && !l.isSpecial);
    return ask(
      "이번 주는 몇 과인가요?",
      list.map((l) => ({ label: lessonName(l), value: l })),
      (picked) => finish(group, picked),
    );
  };

  const pickSemester = (group) => {
    const keys = [...new Set(lessons.filter((l) => !l.isSpecial).map((l) => l.semesterKey))];
    return ask(
      "어느 과정·학기를 하고 계신가요?",
      keys.map((key) => {
        const [course, semester] = key.split("-");
        return { label: `${course}과정 ${semester}학기`, value: key };
      }),
      (semesterKey) => pickLesson(group, semesterKey),
    );
  };

  const groups = HANA_GROUPS.map((g) => ({ label: g.label, value: g.value }));
  if (lesson) return ask(intro, groups, (group) => finish(group, lesson));
  if (preference.value?.group) return ask(intro, [{ label: `${groupLabelOf(preference.value.group)} 그대로`, value: preference.value.group }, ...groups.filter((g) => g.value !== preference.value.group)], pickSemester);
  return ask(intro, groups, pickSemester);
};

// ---------- 질문 기록·답변 평가 (백엔드 BE-2) ----------
// 질문 하나 = 기록 하나(requestId). 답을 마치면 기록을 보내고, 마지막 답변 아래에 평가 버튼을 단다.
// 총회 월간 리포트(질문 수, 많이 찾은 공과, 답하지 못한 질문)에 쓴다. 기록 실패는 답변에 영향을 주지 않는다.
// 개발 서버(localhost)에서는 운영 기록이 쌓이지 않도록 보내지 않는다 (시험할 때만 localStorage chatbotLogDev=1)
const LOG_ENABLED = !/^(localhost|127\.0\.0\.1)$/.test(window.location.hostname) || localStorage.getItem("chatbotLogDev") === "1";

const newRequestId = () =>
  window.crypto?.randomUUID
    ? window.crypto.randomUUID()
    : "xxxxxxxx-xxxx-4xxx-8xxx-xxxxxxxxxxxx".replace(/x/g, () => Math.floor(Math.random() * 16).toString(16));

let currentTurn = null; // { requestId, question, intent, answered, lessonRef, resultCount, firstMessageId }

const startTurn = (question) => {
  currentTurn = { requestId: newRequestId(), question: question.slice(0, 500), intent: "unknown", answered: false, lessonRef: null, resultCount: null, firstMessageId: messageSeq + 1 };
};

const noteTurn = (fields) => {
  if (currentTurn) Object.assign(currentTurn, fields);
};

const sendLog = (turn) => {
  if (!LOG_ENABLED) return;
  axios
    .post("/new_api/open/chatbot/log", {
      requestId: turn.requestId,
      question: turn.question,
      intent: turn.intent,
      answered: turn.answered,
      lessonRef: turn.lessonRef,
      group: preference.value?.group || group.value || null,
      resultCount: turn.resultCount,
    })
    .catch((error) => console.error(error));
};

// "33과 알려 줘"(과정 고르기)·진도 설정처럼 사용자가 골라야 답이 나오는 질문은, 고를 때까지 기록을 열어 둔다
let pendingTurn = null;

const flushPendingTurn = () => {
  if (!pendingTurn) return;
  sendLog(pendingTurn);
  pendingTurn = null;
};

// 고르기 버튼을 눌렀을 때: 열어 둔 질문의 기록을 이어 간다 (이어 갈 기록이 있으면 true)
const resumeTurn = () => {
  if (!pendingTurn || currentTurn) return false;
  currentTurn = pendingTurn;
  pendingTurn = null;
  // 평가 버튼은 실제 답(고른 뒤의 답변)으로 옮긴다
  messages.value.forEach((m) => {
    if (m.requestId === currentTurn.requestId) delete m.requestId;
  });
  currentTurn.firstMessageId = messageSeq + 1;
  return true;
};

const finishTurn = () => {
  const turn = currentTurn;
  currentTurn = null;
  if (!turn) return;
  // 이번 질문에 대한 마지막 봇 답변에 평가 버튼을 단다
  const lastBot = [...messages.value].reverse().find((m) => m.from === "bot" && m.id >= turn.firstMessageId);
  if (lastBot) lastBot.requestId = turn.requestId;
  if (lastBot && (lastBot.choices || lastBot.awaitingPick)) pendingTurn = turn;
  else sendLog(turn);
};

const rateAnswer = (message, rating) => {
  if (message.rating) return;
  message.rating = rating;
  if (!LOG_ENABLED) return;
  axios.post("/new_api/open/chatbot/feedback", { requestId: message.requestId, rating }).catch((error) => console.error(error));
};

// ---------- 답하지 못할 때 ----------
// 엉뚱한 자료를 내놓는 대신, 챗봇이 도울 수 있는 일을 버튼으로 보여 주고 사람 상담으로 연결한다 (F5)

const openKakao = () => window.open(KAKAO_CHANNEL_URL, "_blank", "noopener");

const showHelpOptions = (text = "제가 도울 수 있는 일은 이런 거예요.") =>
  addMessage("bot", {
    text,
    actions: [
      // 버튼 처리(runAction)가 끝난 뒤 질문을 보내야 입력이 막히지 않는다
      ...HELP_EXAMPLES.map((q) => ({
        label: q,
        run: () =>
          setTimeout(() => {
            // 검색 추가 질문을 기다리던 중이면 그 흐름을 끝내야 예시 질문이 바로 답해진다
            firstPrompt.value = "";
            contentType.value = null;
            awaitingGroup = false;
            handleQuestionClick(q);
          }),
      })),
      { label: "카카오톡으로 문의하기", run: openKakao },
    ],
  });

const answerOutOfScope = ({ text, links, external }) => {
  addMessage("bot", {
    text,
    actions: [
      ...links.map((link) => ({ label: link.label, primary: true, run: () => window.open(link.path, "_blank", "noopener") })),
      ...(external ? [{ label: external.label, primary: true, run: () => window.open(external.url, "_blank", "noopener") }] : []),
      { label: "카카오톡으로 문의하기", run: openKakao },
    ],
  });
  showHelpOptions("하나 바이블 수업 준비라면 이렇게 물어보세요.");
};

// ---------- 질문 유형별 응답 ----------

const answerThisWeek = async (lessons, { auto = false, justJoined = false } = {}) => {
  if (!isLoggedIn()) {
    if (auto) return;
    // 가입을 먼저 요구하지 않고 버튼으로 골라 바로 보여 준다 (가입 안내는 자료를 보여 준 뒤)
    const picked = preference.value?.lesson ? findLesson(lessons, preference.value.semesterKey, preference.value.lesson) : null;
    if (picked) {
      await showLessonCard(picked, {
        text: `이번 대화에서 고르신 ${groupLabelOf(preference.value.group)} ${semesterName(picked)} ${lessonName(picked)}예요.`,
        eyebrow: "이번 주 공과",
      });
      return;
    }
    startProgressSetup(lessons, {
      intro: "부서와 진도를 고르시면 이번 주 공과 자료를 바로 보여 드릴게요. 가입하지 않아도 돼요.\n먼저 부서를 골라 주세요.",
    });
    return;
  }
  const pref = await loadPreference();
  // 기준 주일부터 지난 주만큼 과를 넘긴 이번 주 진도 (F1)
  const current = currentProgress(pref);
  const lesson = current?.semesterKey && current?.lesson ? findLesson(lessons, current.semesterKey, current.lesson) : null;

  // 저장된 이번 주 진도 카드
  const showCurrentCard = () => {
    const teacher = `${groupLabelOf(pref.group) ? groupLabelOf(pref.group) + " " : ""}선생님`;
    const name = `${semesterName(lesson)} ${lessonName(lesson)}`;
    // 기준 주일이 없는 예전 저장값은 이번 주가 맞는지 확인받고 기준 주일을 붙인다
    const text = !pref.anchor
      ? `${teacher}, 저장된 진도는 ${name}예요. 이번 주 공과가 맞으면 [맞아요]를 눌러 주세요. 다음 주부터는 자동으로 다음 과로 넘어가요.`
      : current.weeks > 0
        ? `${teacher}, 저장한 진도에서 ${current.weeks}주가 지나 이번 주는 ${name}예요.`
        : `${teacher}, 이번 주 공과를 준비해 두었어요.`;
    return showThisWeekCard(lessons, lesson, text, { confirm: !pref.anchor });
  };

  // 가입·로그인 전에 고른 진도 (한 번만 묻는다)
  const pickedBefore = guestPick?.lesson ? findLesson(lessons, guestPick.semesterKey, guestPick.lesson) : null;
  const pickedGroup = guestPick?.group;
  guestPick = null;
  const savePicked = (text) => async () => {
    try {
      await savePreference(pickedGroup, pickedBefore);
    } catch (error) {
      console.error(error);
      addMessage("bot", "진도를 저장하지 못했어요. 잠시 뒤 다시 시도해 주세요.");
      return false;
    }
    localStorage.setItem(SETUP_OFFERED_KEY, "1");
    await showThisWeekCard(lessons, pickedBefore, text);
  };
  const pickedName = pickedBefore && `${groupLabelOf(pickedGroup)} ${semesterName(pickedBefore)} ${lessonName(pickedBefore)}`;

  if (!lesson) {
    // 저장된 진도가 없으면: 가입 전에 고른 진도를 그대로 저장할지 묻는다
    if (pickedBefore && pickedGroup) {
      addMessage("bot", {
        text: `가입 전에 고르신 ${pickedName}를 우리 반 진도로 저장할까요?`,
        actions: [
          { label: "이 진도로 저장", primary: true, run: savePicked("진도를 저장했어요. 이제 챗봇을 열 때마다 이번 주 공과를 준비해 드릴게요.") },
          { label: "다시 고르기", run: () => startProgressSetup(lessons, { intro: "부서부터 다시 골라 주세요." }) },
        ],
      });
      return;
    }
    // 자동으로 열 때는 한 번만 권한다 (가입하고 막 돌아온 경우는 예외)
    if (auto && !justJoined && localStorage.getItem(SETUP_OFFERED_KEY)) return;
    startProgressSetup(lessons, {
      intro: "우리 반 진도를 저장해 두시면 챗봇을 열 때마다 이번 주 공과를 준비해 드려요. 버튼 세 번이면 끝나요.\n먼저 부서를 골라 주세요.",
    });
    return;
  }

  // 이미 진도가 있는 회원이 로그인 전에 다른 진도를 골랐으면: 어느 쪽으로 할지 묻는다
  if (pickedBefore && pickedGroup && (pickedBefore.lessonRef !== lesson.lessonRef || pickedGroup !== pref.group)) {
    addMessage("bot", {
      text: `로그인 전에 고르신 진도는 ${pickedName}이고, 저장된 이번 주 진도는 ${groupLabelOf(pref.group)} ${semesterName(lesson)} ${lessonName(lesson)}예요. 어느 쪽으로 할까요?`,
      actions: [
        { label: "고른 진도로 바꾸기", primary: true, run: savePicked(`진도를 ${pickedName}로 바꿨어요. 다음 주부터 여기서 이어 가요.`) },
        { label: "저장된 진도 유지", run: showCurrentCard },
      ],
    });
    return;
  }
  await showCurrentCard();
};

const answerLesson = async (lessons, { semesterKey, lesson }) => {
  const found = findLesson(lessons, semesterKey, lesson);
  if (!found) {
    const [course, semester] = semesterKey.split("-");
    addMessage("bot", `${course}과정 ${semester}학기는 ${semester === "1" ? "1~22과" : "23~44과"}로 이루어져 있어요. 과 번호를 다시 확인해 주세요.`);
    noteTurn({ invalid: true });
    return;
  }
  await showLessonCard(found, {
    text: `${semesterName(found)} ${lessonName(found)} '${found.title}'입니다.`,
    actions: isLoggedIn() ? [saveAsThisWeekAction(lessons, found)] : [],
  });
};

const answerSemester = (lessons, { semesterKey }) => {
  const list = lessons.filter((l) => l.semesterKey === semesterKey && !l.isSpecial);
  if (!list.length) return;
  const units = [];
  list.forEach((l) => {
    const last = units[units.length - 1];
    if (last && last.unit === l.unit) last.to = l.lesson;
    else units.push({ unit: l.unit, title: l.unitTitle, from: l.lesson, to: l.lesson });
  });
  addMessage("bot", {
    text:
      `${semesterName(list[0])} 주제는 '${list[0].semesterTitle}'이고, 정규 공과 ${list.length}개로 이루어져 있어요.\n\n` +
      units.map((u) => `${u.unit}단원 ${u.title} (${u.from}~${u.to}과)`).join("\n") +
      `\n\n궁금한 과를 "${semesterName(list[0])} ${list[0].lesson}과"처럼 물어보세요.`,
    note: `출처: 총회 제111회기 홍보 브로슈어 ${list[0].sourcePage}쪽`,
  });
};

const answerLessonNumber = (lessons, { lesson }) => {
  const choices = lessons.filter((l) => l.lesson === lesson);
  addMessage("bot", { text: `${lesson}과는 과정마다 내용이 달라요. 어느 과정의 ${lesson}과인가요?`, choices });
};

// 고민형 질문: 규칙으로 알아들었으면 주제어로, 못 알아들었으면 공과 태그로 찾는다
const answerTopic = (lessons, intent, question) => {
  const found = intent ? searchLessonsByTopic(lessons, intent.words, 3, intent.primary, question) : searchLessonsByTags(lessons, question);
  if (!found.length) return false;
  addMessage("bot", { text: "이런 공과가 도움이 될 거예요. 눌러서 공과 내용과 자료를 확인해 보세요.", choices: found, showKeyPoint: true });
  addMessage("bot", {
    text: "공과가 아닌 다른 자료를 찾으시나요?",
    actions: [{ label: "자료로 검색하기", run: () => searchContents(question) }],
  });
  return true;
};

// 절기 특별 공과: 학기를 알면 바로 카드, 모르면 과정별로 고르게 한다
const answerOccasion = async (lessons, { key, semesterKey }, question) => {
  const found = findOccasionLessons(lessons, key, semesterKey || preference.value?.semesterKey);
  if (!found.length) return false;
  // "성탄 찬양 PPT"처럼 자료를 찾는 말이 있으면 공과와 함께 자료 검색을 권한다
  const offerSearch = () => {
    if (!/ppt|악보|찬양|율동|영상|자료|이미지|설교/i.test(question)) return;
    addMessage("bot", {
      text: `${key} 공과 말고 다른 자료를 찾으시나요?`,
      actions: [{ label: "자료로 검색하기", run: () => searchContents(question) }],
    });
  };
  const exact = found.find((l) => l.semesterKey === (semesterKey || preference.value?.semesterKey));
  if (exact) {
    await showSpecialCard(exact);
    offerSearch();
    return true;
  }
  if (semesterKey) {
    // 물어본 학기에 그 절기 공과가 없을 때
    addMessage("bot", `${semesterKey.replace("-", "과정 ")}학기에는 ${key} 공과가 없어요. 다른 과정의 ${key} 공과를 보여 드릴게요.`);
  }
  addMessage("bot", { text: `${key} 공과는 과정마다 달라요. 어느 과정의 공과인가요?`, choices: found });
  offerSearch();
  return true;
};

const showSpecialCard = (lesson) =>
  showLessonCard(lesson, { text: `${semesterName(lesson)} ${lesson.occasion} 특별 공과 '${lesson.title}'입니다.` });

// 공과 고르기 버튼
const askLesson = async (lesson) => {
  const resumed = resumeTurn();
  addMessage("user", `${semesterName(lesson)} ${lesson.isSpecial ? lesson.occasion + " 공과" : lessonName(lesson)} 알려 줘`);
  isLoading.value = true;
  try {
    if (lesson.isSpecial) await showSpecialCard(lesson);
    else await answerLesson(await loadLessons(), { semesterKey: lesson.semesterKey, lesson: lesson.lesson });
  } finally {
    isLoading.value = false;
    if (resumed) finishTurn();
  }
};

// ---------- AI 질문 이해 (백엔드 BE-3, POST /open/chatbot/intent) ----------
// 규칙·공과 태그로 못 알아들은 질문만 보낸다. AI는 질문을 정해진 칸으로 바꿀 뿐, 답은 아래 공과 데이터로 만든다.
// 2026-10-29 확인: 백엔드가 모든 질문에 intent=unknown 을 돌려준다(모델 설정 확인 요청 중).
// 그동안 부를 때마다 1~2초만 늦어지므로 꺼 두고, 백엔드가 고쳐지면 true 로 바꾼다.
const AI_INTENT_ENABLED = false;
const AI_INTENT_TIMEOUT_MS = 3000;
const AI_INTENT_TYPES = ["lesson", "semester", "lessonNumber", "occasion", "topic", "thisWeek", "service", "adoption", "search"];

// AI 응답 → classifyQuestion 과 같은 모양 (값이 맞지 않으면 null → 자료 검색)
const toIntent = (ai) => {
  if (!ai || !AI_INTENT_TYPES.includes(ai.intent)) return null;
  const semesterKey = /^[1-6]-[12]$/.test(ai.semesterKey || "") ? ai.semesterKey : null;
  const lesson = Number.isInteger(ai.lesson) && ai.lesson >= 1 && ai.lesson <= 44 ? ai.lesson : null;
  const words = Array.isArray(ai.topicWords) ? ai.topicWords.filter((w) => typeof w === "string" && w) : [];
  switch (ai.intent) {
    case "lesson":
      return semesterKey && lesson ? { type: "lesson", semesterKey, lesson } : null;
    case "semester":
      return semesterKey ? { type: "semester", semesterKey } : null;
    case "lessonNumber":
      return lesson ? { type: "lessonNumber", lesson } : null;
    case "occasion":
      return ai.occasionKey ? { type: "occasion", key: ai.occasionKey, semesterKey } : null;
    case "topic":
      return words.length ? { type: "topic", words, primary: words.length } : null;
    case "search":
      return { type: "aiSearch", keywords: Array.isArray(ai.keywords) ? ai.keywords : [], group: HANA_GROUPS.some((g) => g.value === ai.group) ? ai.group : null };
    default:
      return { type: ai.intent };
  }
};

const classifyByAi = async (question) => {
  if (!AI_INTENT_ENABLED) return null;
  try {
    const res = await axios.post(
      "/new_api/open/chatbot/intent",
      { requestId: currentTurn?.requestId || newRequestId(), question: question.slice(0, 200) },
      { timeout: AI_INTENT_TIMEOUT_MS },
    );
    return toIntent(res.data?.data);
  } catch (error) {
    console.error(error); // 늦거나 실패하면 기존 자료 검색으로
    return null;
  }
};

// 공과 데이터로 바로 답할 수 있으면 답하고 true. 아니면 false (자료 검색으로 넘긴다)
// 순서: 규칙 → 공과 태그 → AI 질문 이해 → (못 알아들으면) 자료 검색
const answerFromLessons = async (question) => {
  let intent = classifyQuestion(question);
  if (!intent) {
    try {
      if (answerTopic(await loadLessons(), null, question)) {
        noteTurn({ intent: "topic", answered: true });
        return true;
      }
    } catch (error) {
      console.error(error);
    }
    intent = await classifyByAi(question);
    if (!intent) return false;
  }
  // 답했는지는 실제로 답을 낸 뒤에 정한다 (잘못된 과 번호 안내 등은 답하지 못한 것으로)
  noteTurn({ intent: intent.type === "outOfScope" ? "unknown" : intent.type === "aiSearch" ? "search" : intent.type, answered: false, invalid: false });
  const ok = await answerIntent(intent, question);
  if (ok) noteTurn({ answered: !currentTurn?.invalid });
  return ok;
};

// 알아들은 질문에 답한다. 답하지 못하면 false (자료 검색으로 넘긴다)
const answerIntent = async (intent, question) => {
  if (intent.type === "aiSearch") {
    if (intent.group) group.value = intent.group;
    if (!intent.keywords.length) return false;
    await showSearchResults(intent.keywords, null, question);
    return true;
  }
  if (intent.type === "service") {
    addMessage("bot", { text: SERVICE_INFO, note: `출처: ${SERVICE_INFO_SOURCE}` });
    return true;
  }
  if (intent.type === "outOfScope") {
    answerOutOfScope(intent);
    noteTurn({ invalid: true }); // 안내는 했지만 질문에 답한 것은 아니다
    return true;
  }
  let lessons;
  try {
    lessons = await loadLessons();
  } catch (error) {
    console.error(error);
    return false;
  }
  switch (intent.type) {
    case "adoption":
      addMessage("bot", {
        text: ADOPTION_INFO + (isLoggedIn() ? "" : "\n\n가입하시면 1과정 1학기 자료부터 바로 보실 수 있어요."),
        note: `출처: ${ADOPTION_INFO_SOURCE}`,
        actions: [
          ...(isLoggedIn() ? [] : [{ label: "무료 가입", primary: true, run: goSignup }]),
          { label: "카카오톡으로 문의하기", run: openKakao },
        ],
      });
      if (!isLoggedIn()) shownSignupPrompts.add("adoption");
      return true;
    case "thisWeek":
      await answerThisWeek(lessons);
      return true;
    case "lesson":
      await answerLesson(lessons, intent);
      return true;
    case "occasion":
      return answerOccasion(lessons, intent, question);
    case "semester":
      answerSemester(lessons, intent);
      return true;
    case "lessonNumber":
      answerLessonNumber(lessons, intent);
      return true;
    case "topic":
      return answerTopic(lessons, intent, question);
    default:
      return false;
  }
};

// ---------- 자료 검색 (기존 /nl/v1/chat 흐름) ----------

// 백엔드(BE-1)가 참여 인원 없이도 검색어를 끝까지 만들어 주므로 인원은 묻지도 보내지도 않는다.
// 검색 결과는 백엔드 관련도 정렬(BE-4, prop=relevance)을 그대로 쓴다.
const RESULT_PREVIEW_SIZE = 5;
// 부서·"자료"·"수업" 같은 말은 제목 일치 정렬을 흐리므로 검색어에서 뺀다 (부서는 group 으로 따로 넘긴다)
const GENERIC_KEYWORDS = /^(자료|수업|학생|교사|콘텐츠|추천|영유아부|유치부|유년부|초등부|청소년부|중고등부|대학청년부|장년부)$/;
const searchKeywordsOf = (keywords) => {
  const words = keywords.filter((k) => !GENERIC_KEYWORDS.test(k));
  return words.length ? words : keywords;
};
// 자료 형식을 나타내는 말. 백엔드가 모든 검색어가 맞는 자료가 없어 조건을 넓히면(relaxed)
// "우주선 조립 설명서 PPT" → "PPT"만 맞는 찬양 자료가 나오므로, 주제어(형식이 아닌 말)가 제목에 든 자료만 남긴다
const FORMAT_KEYWORDS = /^(ppt|pdf|hwp|mr|영상|동영상|음원|악보|가사|이미지|사진|파일|자료)$/i;
const RESULT_FETCH_SIZE = 20; // 조건이 넓혀졌을 때 거를 후보를 넉넉히 받는다

const firstPrompt = ref("");
const contentType = ref(null);
// "어느 부서에서 사용하실 예정인가요?"를 물은 상태. 이때 부서가 아닌 말을 하면 새 질문으로 받는다
let awaitingGroup = false;
const GROUP_WORDS = /영아|유아|유치|유년|아동|어린이|초등|청소년|중고등|중등|고등|청년|대학|장년|성인|어른|구역|통합|전체|모든|전\s*부서/;
// 부서 답: 부서 말이 있고, 짧고(15자 이내), 묻는 말투가 아니며, 챗봇이 따로 알아듣는 질문(공과·환불 등)이 아닐 때
// ("통합부서예요"는 도입 문의 규칙에 걸리지만 이 상황에서는 부서 답이다)
const ASKING = /[?？]|나요|까요|인가요|되나요|있어요\?/;
const isGroupAnswer = (text) => {
  if (!GROUP_WORDS.test(text) || text.replace(/\s/g, "").length > 15 || ASKING.test(text)) return false;
  const intent = classifyQuestion(text);
  return !intent || intent.type === "adoption";
};
const group = ref(null);

// 키워드로 찾은 자료를 대화창에 보여 주고, 전체 결과는 기존 결과 페이지로 연결한다
const showSearchResults = async (rawKeywords, resultContentType, question) => {
  const keywords = searchKeywordsOf(rawKeywords);
  const params = new URLSearchParams({ page: 1, size: RESULT_FETCH_SIZE, prop: "relevance", dir: "DESC", pageUnitSize: 10 });
  if (resultContentType != null) params.append("contentType", resultContentType);
  keywords.forEach((keyword) => params.append("keyword", keyword));

  const openFullResults = () => {
    localStorage.setItem("searchKeywords", JSON.stringify(keywords));
    localStorage.setItem("searchQuestion", question);
    if (resultContentType != null) localStorage.setItem("searchContentType", resultContentType);
    else localStorage.removeItem("searchContentType");
    (window.parent !== window ? window.parent : window).location.href = "/mall/ai-result";
  };

  try {
    const res = await axios.get("/new_api/open/item/nl/v1/pagination?" + params.toString());
    const data = res.data?.data;
    const subjectWords = keywords.filter((k) => !FORMAT_KEYWORDS.test(k));
    const relaxed = data?.relaxed === true && subjectWords.length > 0;
    const candidates = data?.items ?? [];
    const items = (relaxed ? candidates.filter((item) => subjectWords.some((w) => (item.title || "").includes(w))) : candidates).slice(0, RESULT_PREVIEW_SIZE);
    noteTurn({ intent: currentTurn?.intent === "unknown" ? "search" : currentTurn?.intent, answered: items.length > 0, invalid: !items.length, resultCount: items.length });
    if (!items.length) {
      showHelpOptions("찾으시는 자료를 아직 찾지 못했어요. 다른 표현으로 물어보시거나, 아래처럼 물어보세요. 찾는 자료가 꼭 필요하시면 카카오톡으로 알려 주세요.");
      return;
    }
    addMessage("bot", {
      text: relaxed
        ? `'${keywords.join(", ")}'가 모두 들어간 자료는 없어서, '${subjectWords.join(", ")}' 중 일부가 들어간 자료를 골랐어요.`
        : `'${keywords.join(", ")}' 관련 자료예요.`,
      items,
      total: relaxed ? items.length : data.pageInfo?.total ?? items.length,
      // 조건을 넓힌 결과는 전체 결과 페이지에 관계없는 자료가 섞이므로 걸러 낸 것만 보여 준다
      actions: relaxed ? [] : [{ label: "전체 결과 보기", primary: true, run: openFullResults }],
    });
  } catch (error) {
    console.error(error);
    addMessage("bot", { text: "자료를 불러오지 못했어요. 잠시 뒤 다시 시도해 주세요.", actions: [{ label: "카카오톡으로 문의하기", run: openKakao }] });
  }
};

const searchContents = async (searchQuery) => {
  isLoading.value = true;
  // 저장된 부서(회원)나 이번 대화에서 고른 부서(비회원)가 있으면 "어느 부서인가요?"를 다시 묻지 않는다
  if (group.value == null && preference.value?.group) group.value = preference.value.group;
  const params = new URLSearchParams();
  params.append("naturalLanguage", searchQuery);
  if (firstPrompt.value) params.append("firstPrompt", firstPrompt.value);
  if (contentType.value != null) params.append("contentType", contentType.value);
  if (group.value != null) params.append("group", group.value);

  try {
    const response = await axios.get("/new_api/open/item/nl/v1/chat?" + params.toString());
    const data = response.data;
    if (data.code != 200) {
      showHelpOptions("죄송해요, 자료 검색에 문제가 생겼어요. 아래 질문은 바로 답해 드릴 수 있어요.");
      return;
    }
    const resData = data.data;

    // 응답에서 누적 파라미터 업데이트
    if (resData.contentType != null) contentType.value = resData.contentType;
    if (resData.group != null) group.value = resData.group;

    // 대화가 완료되고 부서가 확보되면 결과를 대화창에 보여 준다
    if (resData.finished && group.value != null && resData.keywords && resData.keywords.length > 0) {
      // 추가 질문에 답한 경우 마지막 답변("초등부요")이 아닌 최초 질문을 결과 제목으로 쓴다
      const question = firstPrompt.value || searchQuery;
      await showSearchResults(resData.keywords, resData.contentType, question);
      // 다음 질문은 새 검색으로 시작한다 (부서는 유지)
      firstPrompt.value = "";
      contentType.value = null;
      awaitingGroup = false;
      return;
    }

    // 추가 정보가 필요한 경우 - firstPrompt 저장 및 추가 질문 표시
    if (!firstPrompt.value) firstPrompt.value = searchQuery;
    noteTurn({ intent: "search" }); // 부서 등을 묻는 중인 자료 검색 (중간에 접히면 답하지 못한 검색으로 남는다)

    // 부서가 없으면 부서를 묻는다 (부서 예시를 함께 보여 준다)
    const additionalQuestion = resData.naturalLanguageAdditionalQuestion;
    awaitingGroup = !group.value;
    if (!group.value) {
      addMessage("bot", "어느 부서에서 사용하실 예정인가요? (예: 영유아부, 유치부, 유년부, 초등부, 청소년부, 대학청년부)");
    } else if (additionalQuestion) {
      addMessage("bot", additionalQuestion);
    } else {
      addMessage("bot", "찾으시는 자료를 조금 더 구체적으로 알려주세요. (예: 설교 PPT, 찬양 음원, 활동 자료)");
    }
  } catch (error) {
    console.error(error);
    showHelpOptions("죄송해요, 자료 검색에 문제가 생겼어요. 아래 질문은 바로 답해 드릴 수 있어요.");
  } finally {
    isLoading.value = false;
  }
};

// ---------- 입력 ----------

const handleSendMessage = async () => {
  const question = userInput.value.trim();
  if (!question || isLoading.value) return;
  // 부서를 묻는 중에 부서가 아닌 말("환불하고 싶어요" 등)을 하면 앞 검색은 접고 새 질문으로 받는다
  if (firstPrompt.value && awaitingGroup && !isGroupAnswer(question)) {
    const dropped = currentTurn;
    currentTurn = null;
    if (dropped) sendLog(dropped); // 앞 검색은 답하지 못한 질문으로 남긴다
    firstPrompt.value = "";
    contentType.value = null;
    awaitingGroup = false;
  }
  // 검색 추가 질문(부서 등)에 답하는 중이면 처음 질문의 기록을 이어 간다
  if (!firstPrompt.value || !currentTurn) {
    flushPendingTurn(); // 고르지 않고 새로 물으면 앞 질문 기록을 그대로 보낸다
    startTurn(question);
  }
  addMessage("user", question);
  userInput.value = "";

  if (firstPrompt.value) {
    // 자료 검색 중 추가 질문(부서 등)에 답하는 중이면 검색 흐름을 이어 간다
    await searchContents(question);
  } else {
    isLoading.value = true;
    let answered = false;
    try {
      answered = await answerFromLessons(question);
    } catch (error) {
      console.error(error);
    } finally {
      isLoading.value = false;
    }
    if (!answered) await searchContents(question);
  }

  // 추가 질문(부서 등)을 기다리는 중이 아니면 답변 하나로 센다
  if (!firstPrompt.value) {
    finishTurn();
    answeredCount.value += 1;
    // 다른 가입 안내를 한 번도 못 본 사람에게만 (연달아 안내하지 않도록)
    if (answeredCount.value >= GENERIC_PROMPT_AFTER && shownSignupPrompts.size === 0) {
      promptSignupOnce("generic", "자주 쓰신다면 가입해 두세요. 진도 저장과 무료 자료 받기를 함께 쓸 수 있어요.");
    }
  }
};

const handleQuestionClick = (question) => {
  userInput.value = question;
  handleSendMessage();
};

const displayedQuestions = ref(pickChatbotQuestions(4));

onMounted(async () => {
  window.addEventListener("pagehide", flushPendingTurn);
  // 가입·로그인을 다녀왔으면 하던 대화를 먼저 되살린다
  const restored = restoreChatSnapshot();

  // URL 쿼리 파라미터(`?q=...`)로 초기 질문이 전달되면 자동 전송
  // toTopComponent / MainInquirySection 진입점에서 동일한 챗봇 흐름을 재사용하기 위함
  let initialQuery = "";
  try {
    initialQuery = (new URLSearchParams(window.location.search).get("q") || "").trim();
  } catch (e) {
    // URL 파싱 실패는 무시하고 일반 진입 흐름 유지
  }
  if (initialQuery) {
    // 로그인 교사는 저장된 부서로 자료를 보여 주도록 설정을 먼저 읽는다
    if (isLoggedIn()) await loadPreference();
    userInput.value = initialQuery;
    handleSendMessage();
    return;
  }

  // 로그인한 교사는 저장된 진도로 이번 주 공과를 먼저 보여 준다
  if (!isLoggedIn()) return;
  isLoading.value = true;
  try {
    // 비회원으로 하던 대화가 있으면 로그인했다고 알리고 이어 간다 (막 가입한 회원이면 진도 설정을 권한다)
    const justJoined = restored && shownSignupPrompts.size > 0;
    if (restored) addMessage("bot", "로그인되었어요. 하던 대화에 이어서 도와드릴게요.");
    await answerThisWeek(await loadLessons(), { auto: true, justJoined });
  } catch (error) {
    console.error(error);
  } finally {
    isLoading.value = false;
  }
});
</script>
